import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Grid, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Product from '../components/Product';
import { useLocation } from 'react-router-dom';

const Trending = () => {
  const { user, api } = useAuth();
  const location = useLocation();
  const [products, setProducts] = useState(location.state?.products || []);
  const [loading, setLoading] = useState(!location.state?.products);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(!location.state?.products);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const observer = useRef();
  const loadingRef = useRef(null);

  // Set categories based on user gender
  useEffect(() => {
    if (user?.gender) {
      if (user.gender.toLowerCase() === 'women') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'dresses', name: 'Dresses' },
          { id: 'tops', name: 'Tops' },
          { id: 'pants', name: 'Pants' },
          { id: 'skirts', name: 'Skirts' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'activewear', name: 'Activewear' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'bags', name: 'Bags' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'lingerie', name: 'Lingerie' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      } else if (user.gender.toLowerCase() === 'men') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'pants', name: 'Pants' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'activewear', name: 'Activewear' },
          { id: 'suits', name: 'Suits' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'underwear', name: 'Underwear' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      }
    }
  }, [user?.gender]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setPage(1);
    setProducts([]);
    setHasMore(true);
    setInitialLoad(true);
  };

  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/api/products/trending?page=${page}&category=${activeCategory}`);
        const newProducts = response.data.products || [];
        setHasMore(newProducts.length > 0);
        setProducts(prevProducts => {
          if (page === 0) return newProducts;
          return [...prevProducts, ...newProducts];
        });
      } catch (err) {
        console.error('Error fetching trending products:', err);
        setError('Failed to load trending products. Please try again later.');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    if (user) {
      fetchProducts();
    }
  }, [user, api, page, activeCategory]);

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto', pb: 8 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Trending Now 🌟
      </Typography>

      {/* Category Carousel */}
      <Box 
        sx={{ 
          mb: 3, 
          pb: 1,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {categories.map((category) => (
          <Button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            sx={{
              mr: 2,
              px: 2,
              py: 1,
              color: 'text.primary',
              backgroundColor: 'white',
              border: 'none',
              borderRadius: 0,
              borderBottom: activeCategory === category.id ? 2 : 0,
              borderColor: 'primary.main',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'white',
                opacity: 0.8
              }
            }}
          >
            {category.name}
          </Button>
        ))}
      </Box>

      {initialLoad && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {products.map((product, index) => {
              // Handle different image URL structures
              let imageUrl = '';
              
              // Try media.images structure first
              if (product.media?.images && product.media.images.length > 0) {
                const primaryImage = product.media.images.find(img => img.isPrimary)?.url;
                imageUrl = primaryImage || product.media.images[0]?.url;
              }
              
              // Fallback to image_url if exists
              if (!imageUrl && product.image_url) {
                imageUrl = product.image_url;
              }
              
              // Fallback to imageUrl if exists
              if (!imageUrl && product.imageUrl) {
                imageUrl = product.imageUrl;
              }

              // Get original price from product_details
              const originalPrice = product.product_details?.original_price || product.original_price;

              // Calculate discount percentage if not provided
              let discountPercentage = product.discount_percentage;
              if (!discountPercentage && originalPrice && product.price) {
                discountPercentage = Math.round(((originalPrice - product.price) / originalPrice) * 100);
              }

              return (
                <Grid 
                  item 
                  xs={6} 
                  sm={6} 
                  md={6} 
                  key={product.id}
                  ref={index === products.length - 1 ? lastProductElementRef : null}
                >
                  <Product 
                    id={product.id}
                    image={imageUrl}
                    brand={product.brand?.name || product.brandName || product.brand}
                    name={product.title || product.name}
                    price={product.price}
                    originalPrice={originalPrice}
                    discountPercentage={discountPercentage > 0 ? discountPercentage : undefined}
                    productDetails={product.product_details}
                    variants={product.variants}
                    category={product.product_details?.productType?.name || product.category}
                    color={product.variants?.[0]?.color || product.color}
                    product={product}
                  />
                </Grid>
              );
            })}
          </Grid>

          {error && (
            <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          {!initialLoad && loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, mb: 2 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Loading more products...
              </Typography>
            </Box>
          )}

          {!hasMore && products.length > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                mt: 4, 
                color: 'text.secondary',
                borderTop: '1px solid',
                borderColor: 'divider',
                pt: 2
              }}
            >
              You've reached the end of the list
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default Trending; 